import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { PresentacionProductoComercializador } from '../interfaces/producto-comercializador.interface';
import { ProductTypeResponseInterface } from 'src/interfaces/product-type-response.interface';

/**
 * Servicio para gestión de productos comercializadores
 * @since 1.0.0
 */
@Injectable({
    providedIn: 'root',
})
export class ProductosComercializadoresService {
    constructor(private readonly http: HttpClient) {}

    private readonly apiSiriUrl = environment.apiSiriUrl;

    /**
     * Obtiene presentaciones por tipos de producto
     * @param tiposProductoIds - ID o array de IDs de tipos de producto
     * @param idAgenteVigila - ID del agente vigilado
     * @returns Promise con array de presentaciones
     */
    async getPresentacionesPorTipos(
        tiposProductoIds: number | number[],
        idAgenteVigilado: string
    ): Promise<PresentacionProductoComercializador[]> {
        try {
            const idsArray = Array.isArray(tiposProductoIds)
                ? tiposProductoIds
                : [tiposProductoIds];
            const idsString = idsArray.join(',');
            const url = `${this.apiSiriUrl}registros-comercializador/presentaciones-por-tipos/${idsString}/${idAgenteVigilado}`;

            return await firstValueFrom(
                this.http.get<PresentacionProductoComercializador[]>(url)
            );
        } catch (error) {
            console.error('Error en getPresentacionesPorTipos:', error);
            throw error;
        }
    }

    /**
     * Obtiene todos los tipos de producto
     * @returns Promise con array de tipos de producto
     */
    async getTypeProduct(): Promise<ProductTypeResponseInterface[]> {
        try {
            return await firstValueFrom(
                this.http.get<ProductTypeResponseInterface[]>(
                    `${this.apiSiriUrl}tipo-producto`
                )
            );
        } catch (error) {
            console.error('Error al cargar tipos de producto', error);
            throw error;
        }
    }

    /**
     * Obtiene los tipos de producto disponibles para un agente vigilado,
     * según el tipo de producto que importa la empresa (HU147).
     * @param idAgenteVigilado - ID del agente vigilado
     * @returns Promise con array de tipos de producto disponibles
     */
    async getTiposDisponibles(
        idAgenteVigilado: string
    ): Promise<ProductTypeResponseInterface[]> {
        try {
            return await firstValueFrom(
                this.http.get<ProductTypeResponseInterface[]>(
                    `${this.apiSiriUrl}tipo-producto/disponibles/${idAgenteVigilado}`
                )
            );
        } catch (error) {
            console.error(
                'Error al cargar tipos de producto disponibles',
                error
            );
            throw error;
        }
    }

    /**
     * Guarda las presentaciones seleccionadas
     * @param data - Datos de presentaciones a guardar
     * @returns Promise con respuesta del servidor
     */
    async postPresentacionesSeleccionadas(data: any): Promise<any> {
        try {
            return await firstValueFrom(
                this.http.post<any>(
                    `${this.apiSiriUrl}registros-comercializador`,
                    data
                )
            );
        } catch (error) {
            console.error(
                'Error al guardar presentaciones seleccionadas',
                error
            );
            throw error;
        }
    }
}
